import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import '../styles/TicketDesign.css';

interface TicketElement {
  id: string;
  type: 'text' | 'image';
  content: string;
  x: number;
  y: number;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  width?: number;
  height?: number;
  scale?: number;
}

function TicketDesign() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [bookingData, setBookingData] = useState<any>(null);
  const [elements, setElements] = useState<TicketElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ width: 0, height: 0, x: 0, y: 0 });

  const fonts = [
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, serif' },
    { name: 'Cairo', value: 'Cairo, sans-serif' },
    { name: 'Amiri', value: 'Amiri, serif' },
    { name: 'Tajawal', value: 'Tajawal, sans-serif' }
  ];

  const { bookingCode } = useParams();

  useEffect(() => {
    if (bookingCode) {
      // Fetch booking from backend
      fetch(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/bookings/${bookingCode}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setBookingData(data.booking);
          } else {
            alert('لم يتم العثور على الحجز');
            navigate('/');
          }
        })
        .catch(err => {
          console.error(err);
          navigate('/');
        });
    } else {
      const data = localStorage.getItem('bookingData');
      if (data) {
        setBookingData(JSON.parse(data));
      } else {
        navigate('/');
      }
    }
  }, [navigate, bookingCode]);

  const addText = () => {
    const newElement: TicketElement = {
      id: Date.now().toString(),
      type: 'text',
      content: 'نص جديد',
      x: 50,
      y: 50,
      fontSize: 24,
      fontFamily: 'Cairo, sans-serif',
      color: '#000000'
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        // التحقق من حجم الملف (الحد الأقصى 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert('حجم الصورة كبير جداً. الحد الأقصى هو 5MB');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            // تصغير الصورة إذا كانت كبيرة جداً
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            
            // الحد الأقصى لأبعاد الصورة - زيادة للجودة العالية
            const maxWidth = 600;
            const maxHeight = 600;
            let width = img.width;
            let height = img.height;
            
            // تصغير الصورة مع المحافظة على النسبة
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width *= ratio;
              height *= ratio;
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx!.drawImage(img, 0, 0, width, height);
            
            // تحويل إلى base64 بصيغة PNG عالية الجودة للطباعة
            const compressedImage = canvas.toDataURL('image/png');
            
            // حساب الحجم الأولي للعرض
            const displaySize = 150;
            let displayWidth = width;
            let displayHeight = height;
            
            if (displayWidth > displayHeight) {
              if (displayWidth > displaySize) {
                displayHeight = (displayHeight / displayWidth) * displaySize;
                displayWidth = displaySize;
              }
            } else {
              if (displayHeight > displaySize) {
                displayWidth = (displayWidth / displayHeight) * displaySize;
                displayHeight = displaySize;
              }
            }
            
            const newElement: TicketElement = {
              id: Date.now().toString(),
              type: 'image',
              content: compressedImage,
              x: 50,
              y: 50,
              width: Math.round(displayWidth),
              height: Math.round(displayHeight)
            };
            setElements([...elements, newElement]);
            setSelectedElement(newElement.id);
          };
          img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const updateElement = (id: string, updates: Partial<TicketElement>) => {
    setElements(elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };

  const deleteElement = (id: string) => {
    setElements(elements.filter(el => el.id !== id));
    setSelectedElement(null);
  };

  const handleMouseDown = (e: React.MouseEvent, elementId: string) => {
    e.preventDefault();
    setSelectedElement(elementId);
    setIsDragging(true);
    
    const element = elements.find(el => el.id === elementId);
    if (element) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left - element.x,
          y: e.clientY - rect.top - element.y
        });
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent, elementId: string) => {
    e.preventDefault();
    setSelectedElement(elementId);
    setIsDragging(true);
    
    const element = elements.find(el => el.id === elementId);
    if (element && e.touches.length > 0) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const touch = e.touches[0];
        setDragOffset({
          x: touch.clientX - rect.left - element.x,
          y: touch.clientY - rect.top - element.y
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && selectedElement && !isResizing) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left - dragOffset.x;
        const y = e.clientY - rect.top - dragOffset.y;
        updateElement(selectedElement, { x, y });
      }
    } else if (isResizing && selectedElement) {
      const element = elements.find(el => el.id === selectedElement);
      if (element && element.type === 'image') {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const aspectRatio = resizeStart.width / resizeStart.height;
        
        // حساب الحجم الجديد بناءً على التغيير الأكبر
        const delta = Math.max(deltaX, deltaY);
        let newWidth = resizeStart.width + delta;
        let newHeight = newWidth / aspectRatio;
        
        // تحديد الحد الأدنى للحجم فقط مع المحافظة على النسبة
        if (newWidth < 50) {
          newWidth = 50;
          newHeight = newWidth / aspectRatio;
        }
        
        updateElement(selectedElement, { width: Math.round(newWidth), height: Math.round(newHeight) });
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (isDragging && selectedElement && !isResizing && e.touches.length > 0) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const touch = e.touches[0];
        const x = touch.clientX - rect.left - dragOffset.x;
        const y = touch.clientY - rect.top - dragOffset.y;
        updateElement(selectedElement, { x, y });
      }
    } else if (isResizing && selectedElement && e.touches.length > 0) {
      const element = elements.find(el => el.id === selectedElement);
      if (element && element.type === 'image') {
        const touch = e.touches[0];
        const deltaX = touch.clientX - resizeStart.x;
        const deltaY = touch.clientY - resizeStart.y;
        const aspectRatio = resizeStart.width / resizeStart.height;
        
        // حساب الحجم الجديد بناءً على التغيير الأكبر
        const delta = Math.max(deltaX, deltaY);
        let newWidth = resizeStart.width + delta;
        let newHeight = newWidth / aspectRatio;
        
        // تحديد الحد الأدنى للحجم فقط مع المحافظة على النسبة
        if (newWidth < 50) {
          newWidth = 50;
          newHeight = newWidth / aspectRatio;
        }
        
        updateElement(selectedElement, { width: Math.round(newWidth), height: Math.round(newHeight) });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    // إلغاء التحديد إذا تم النقر على المساحة الفارغة
    if (e.target === e.currentTarget) {
      setSelectedElement(null);
    }
  };

  const handleSubmit = async () => {
    // أخذ صورة من مساحة التصميم
    if (canvasRef.current) {
      try {
        // إلغاء تحديد أي عنصر لإزالة الإطارات وأزرار الحذف
        const previousSelectedElement = selectedElement;
        setSelectedElement(null);
        
        // انتظار قليلاً للتأكد من تحديث واجهة المستخدم
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // إخفاء QR Code مؤقتاً (سيتم إضافته لاحقاً لكل تذكرة)
        const qrElement = canvasRef.current.querySelector('.qrcode-placeholder');
        if (qrElement) {
          (qrElement as HTMLElement).style.display = 'none';
        }
        
        // أخذ صورة من التصميم بجودة محسّنة (متوازنة)
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: '#FFFFFF',
          scale: 2, // جودة عالية لكن ضمن حدود timeout
          useCORS: true,
          logging: false,
          allowTaint: true,
          height: canvasRef.current.offsetHeight,
          width: canvasRef.current.offsetWidth,
          scrollX: 0,
          scrollY: 0
        });
        
        // إظهار QR Code مرة أخرى
        if (qrElement) {
          (qrElement as HTMLElement).style.display = 'block';
        }
        
        // إعادة تحديد العنصر السابق إذا لزم الأمر
        setSelectedElement(previousSelectedElement);
        
        // تحويل Canvas إلى base64 بأعلى جودة PNG (بدون ضغط)
        const imageData = canvas.toDataURL('image/png'); // PNG بجودة 100% بدون ضغط
        
        // حفظ الصورة كتصميم
        const ticketDesign = {
          backgroundImage: imageData,
          width: canvasRef.current.offsetWidth,
          height: canvasRef.current.offsetHeight,
          timestamp: new Date().toISOString()
        };
        
        if (bookingCode) {
          // This is an external flow! Send it to the backend immediately.
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/bookings/code/${bookingCode}/design`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ ticketDesign })
            });
            const data = await response.json();
            if (data.success) {
              alert('تم رفع التصميم الخاص بك بنجاح! شكراً لك.');
              navigate('/');
            } else {
              alert('حدث خطأ في رفع التصميم.');
            }
          } catch(err) {
             console.error(err);
             alert('حدث خطأ أثناء الاتصال بالخادم.');
          }
        } else {
          // إضافة تصميم التذاكر إلى بيانات الحجز (Local flow)
          const updatedBookingData = {
            ...bookingData,
            ticketDesign: ticketDesign
          };
          
          localStorage.setItem('bookingData', JSON.stringify(updatedBookingData));
          
          // الانتقال إلى صفحة الدفع
          navigate('/payment');
        }
      } catch (error) {
        console.error('Error capturing design:', error);
        alert('حدث خطأ في حفظ التصميم. يرجى المحاولة مرة أخرى.');
      }
    }
  };

  const selectedElementData = elements.find(el => el.id === selectedElement);

  return (
    <motion.div 
      className="ticket-design-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <div className="page-title">
            تصميم التذاكر
          </div>
          <button onClick={handleSubmit} className="save-button">
            حفظ وإنهاء
          </button>
        </div>
      </nav>

      <div className="design-container">
        {/* الشريط الجانبي */}
        <div className="sidebar">
          <h3>الأدوات</h3>
          
          <div className="tool-section">
            <button onClick={addText} className="tool-button">
              <span className="tool-icon">T</span>
              إضافة نص
            </button>
            
            <button onClick={addImage} className="tool-button">
              <span className="tool-icon">🖼️</span>
              إضافة صورة
            </button>
          </div>

          {selectedElementData && (
            <div className="properties-section">
              <h4>خصائص العنصر</h4>
              
              {selectedElementData.type === 'text' && (
                <>
                  <div className="property-group">
                    <label>النص:</label>
                    <input
                      type="text"
                      value={selectedElementData.content}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { content: e.target.value })}
                    />
                  </div>

                  <div className="property-group">
                    <label>الخط:</label>
                    <select
                      value={selectedElementData.fontFamily}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { fontFamily: e.target.value })}
                    >
                      {fonts.map(font => (
                        <option key={font.value} value={font.value}>
                          {font.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="property-group">
                    <label>حجم الخط:</label>
                    <input
                      type="number"
                      value={selectedElementData.fontSize}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { fontSize: parseInt(e.target.value) })}
                      min="10"
                      max="72"
                    />
                  </div>

                  <div className="property-group">
                    <label>اللون:</label>
                    <input
                      type="color"
                      value={selectedElementData.color}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { color: e.target.value })}
                    />
                  </div>
                </>
              )}

              {selectedElementData.type === 'image' && (
                <>
                  <div className="property-group">
                    <label>العرض:</label>
                    <input
                      type="number"
                      value={selectedElementData.width}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { width: parseInt(e.target.value) })}
                      min="10"
                    />
                  </div>

                  <div className="property-group">
                    <label>الارتفاع:</label>
                    <input
                      type="number"
                      value={selectedElementData.height}
                      onChange={(e) => selectedElement && updateElement(selectedElement, { height: parseInt(e.target.value) })}
                      min="10"
                    />
                  </div>

                </>
              )}

            </div>
          )}

          <div className="booking-info">
            <h4>معلومات الحجز</h4>
            <p><strong>الاسم:</strong> {bookingData?.customerName}</p>
            <p><strong>المناسبة:</strong> {bookingData?.eventLocation}</p>
            <p><strong>التاريخ:</strong> {bookingData?.eventDate}</p>
            <p><strong>عدد التذاكر:</strong> {bookingData?.totalTickets}</p>
          </div>
        </div>

        {/* مساحة التصميم */}
        <div className="design-area">
          <div className="canvas-container">
            <div 
              ref={canvasRef}
              className="ticket-canvas"
              onClick={handleCanvasClick}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* عناصر التصميم */}
              {elements.map(element => (
                <div
                  key={element.id}
                  className={`design-element ${selectedElement === element.id ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    left: element.x,
                    top: element.y,
                    cursor: isDragging && selectedElement === element.id ? 'grabbing' : 'grab'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => handleMouseDown(e, element.id)}
                  onTouchStart={(e) => handleTouchStart(e, element.id)}
                >
                  {element.type === 'text' ? (
                    <div
                      style={{
                        fontSize: element.fontSize,
                        fontFamily: element.fontFamily,
                        color: element.color,
                        userSelect: 'none'
                      }}
                    >
                      {element.content}
                    </div>
                  ) : (
                    <>
                      <img
                        src={element.content}
                        alt="Uploaded"
                        style={{
                          width: element.width,
                          height: element.height,
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      />
                      {selectedElement === element.id && (
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setIsResizing(true);
                            setResizeStart({
                              width: element.width || 100,
                              height: element.height || 100,
                              x: e.clientX,
                              y: e.clientY
                            });
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            if (e.touches.length > 0) {
                              setIsResizing(true);
                              setResizeStart({
                                width: element.width || 100,
                                height: element.height || 100,
                                x: e.touches[0].clientX,
                                y: e.touches[0].clientY
                              });
                            }
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {/* زر الحذف X */}
                  {selectedElement === element.id && (
                    <button
                      className="delete-x-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteElement(element.id);
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteElement(element.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              {/* QR Code */}
              <div className="qrcode-placeholder">
                <div className="qrcode-container">
                  <QRCode
                    value="FOTOMISTA-SAMPLE-TICKET"
                    size={100}
                    level="M"
                  />
                </div>
                <div className="qrcode-text">مسح التذكرة</div>
              </div>
              
              {/* الشعار في الزاوية */}
              <div className="corner-logo">
                <img src="/images/logo.png" alt="Fotomista" />
              </div>
            </div>
          </div>

          <div className="design-instructions">
            <p>قم بتصميم التذكرة عن طريق إضافة النصوص والصور من الشريط الجانبي</p>
            <p>يمكنك سحب العناصر لتحريكها في مساحة التصميم</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default TicketDesign;